import { createSelector } from '../reselect';
import { getStats, total, simpleChart } from '../generalUtils';

const createBasicSelector = (field) => createSelector([getStats], (stats) => total(stats, field));
const createBasicAvgSelector = (field) =>
  createSelector([getStats], (stats) => avg(stats, field) || 0);

function avg(stats, param) {
  if (!stats) return 0;
  return (
    stats.reduce(
      (dayTotal, day) =>
        dayTotal +
        Object.keys(day.hours).reduce(
          (hourTotal, hour) => hourTotal + (day.hours[hour][param] || 0),
          0
        ) /
          Object.keys(day.hours).length,
      0
    ) / stats.length
  );
}

const facebookLikes = createBasicSelector('social_fb_likes');
const facebookComments = createBasicSelector('social_fb_comments');
const facebookShares = createBasicSelector('social_fb_shares');
const facebookPosts = createBasicSelector('social_fb_posts');
const facebookFollowers = createBasicSelector('social_fb_followers');
const facebookSocialImpressions = createBasicSelector('social_fb_impressions');
const facebookSocialEengagements = createBasicSelector('social_fb_engagements');
const facebookSocialAvg = createBasicAvgSelector('social_fb_avg');
const facebookSocialAvgDaily = createSelector(
  [facebookLikes, facebookComments, facebookShares, facebookPosts, facebookFollowers],
  (facebookLikes, facebookComments, facebookShares, facebookPosts, facebookFollowers) =>
    (facebookLikes + facebookComments + facebookShares) /
    (facebookPosts || 1) /
    (facebookFollowers || 1)
);
const facebookReach = createBasicSelector('social_fb_reach');
const facebookProfileView = createBasicSelector('social_fb_profile_view');
const facebookWebsiteClicks = createBasicSelector('social_fb_clicks');
const instagramLikes = createBasicSelector('social_ig_likes');
const instagramComments = createBasicSelector('social_ig_comments');
const instagramShares = createBasicSelector('social_ig_shares');
const instagramPosts = createBasicSelector('social_ig_posts');
const instagramFollowers = createBasicSelector('social_ig_followers');
const instagramImpressions = createBasicSelector('social_ig_impressions');
const instagramAvg = createBasicAvgSelector('social_ig_avg');
const instagramAvgImpressionsFollowers = createBasicAvgSelector(
  'social_ig_avg_impressions_followers'
);
const instagramAvg_daily = createSelector(
  [instagramLikes, instagramComments, instagramShares, instagramPosts, instagramFollowers],
  (instagramLikes, instagramComments, instagramShares, instagramPosts, instagramFollowers) =>
    (instagramLikes + instagramComments + instagramShares) /
    (instagramPosts || 1) /
    (instagramFollowers || 1)
);
const instagramReach = createBasicSelector('social_ig_reach');
const instagramProfileView = createBasicSelector('social_ig_profile_view');
const instagramWebsiteClicks = createBasicSelector('social_ig_clicks');

const chartFacebookLikes = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_likes')
);
const chartFacebookComments = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_comments')
);
const chartFacebookShares = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_shares')
);
const chartFacebookPosts = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_posts')
);
const chartFacebookFollowers = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_followers')
);
const chartFacebookImpressions = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_impressions')
);
const chartFacebookSocialEengagements = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_engagements')
);
const chartFacebookReach = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_reach')
);
const chartFacebookProfileView = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_profile_view')
);
const chartFacebookWebsiteClicks = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_clicks')
);
const chartFacebookSocialAvg = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_fb_avg')
);

const chartInstagramLikes = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_likes')
);
const chartInstagramComments = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_comments')
);
const chartInstagramShares = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_shares')
);
const chartInstagramPosts = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_posts')
);
const chartInstagramFollowers = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_followers')
);
const chartInstagramImpressions = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_impressions')
);
const chartInstagramReach = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_reach')
);
const chartInstagramProfileView = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_profile_view')
);
const chartInstagramWebsiteClicks = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_clicks')
);
const chartInstagramAvg = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_avg')
);
const chartInstagramAvgImpressionsFollowers = createSelector([getStats], (stats) =>
  simpleChart(stats, 'social_ig_avg_impressions_followers')
);

const PROPS_TO_SELECTORS = {};

export default {
  PROPS_TO_SELECTORS,
  SELECTORS: {
    facebookLikes,
    facebookComments,
    facebookShares,
    facebookPosts,
    facebookFollowers,
    facebookSocialImpressions,
    facebookSocialEengagements,
    facebookSocialAvg,
    facebookReach,
    facebookProfileView,
    facebookWebsiteClicks,
    chartFacebookLikes,
    chartFacebookComments,
    chartFacebookShares,
    chartFacebookPosts,
    chartFacebookFollowers,
    chartFacebookImpressions,
    chartFacebookSocialEengagements,
    chartFacebookReach,
    chartFacebookProfileView,
    chartFacebookWebsiteClicks,
    chartFacebookSocialAvg,
    instagramLikes,
    instagramComments,
    instagramShares,
    instagramPosts,
    instagramFollowers,
    instagramImpressions,
    instagramAvg,
    instagramAvgImpressionsFollowers,
    instagramReach,
    instagramProfileView,
    instagramWebsiteClicks,
    chartInstagramLikes,
    chartInstagramComments,
    chartInstagramShares,
    chartInstagramPosts,
    chartInstagramFollowers,
    chartInstagramImpressions,
    chartInstagramReach,
    chartInstagramProfileView,
    chartInstagramWebsiteClicks,
    chartInstagramAvg,
    chartInstagramAvgImpressionsFollowers,
  },
};
